export { LicenseKeysPanel }
export type { LicenseMap }

import { useState } from 'react'
import type { FormEvent } from 'react'
import { describeUnexpectedError } from '../../components/describeError.ts'
import { isNotLoggedInAbort } from '../../components/abortValue.ts'
import { clearWhenSettled } from '../../components/clearWhenSettled.ts'
import { linkStyle } from '../../components/messageStyles.ts'
import { LicenseSnippet } from '../../shared/LicenseSnippet.tsx'
import { onGenerateLicenseKey } from './LicenseKeysPanel.telefunc.ts'
import { panelStyle as style } from './styles.ts'
import './snippet.css'

// Paid-customer license map — values are always forever-strings (no `{expires, key}`
// time-limited shape). Narrower than `wire.ts`'s `LicenseMap` (which carries the
// trial-side time-limited variant); the wider `renderLicenseSnippetLines` accepts both.
type LicenseMap = Record<string, string>

type Result =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success'; addedDomains: string[] }
  | { kind: 'inputErrors'; message: string }
  | { kind: 'error'; message: string }

function LicenseKeysPanel({ license, onUpdate }: { license: LicenseMap; onUpdate: (next: LicenseMap) => void }) {
  const [domainsInput, setDomainsInput] = useState('')
  const [result, setResult] = useState<Result>({ kind: 'idle' })
  const count = Object.keys(license).length

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (result.kind === 'submitting') return
    // Empty submit is blocked by the input's `required`; this guards whitespace-only input.
    // The "enter at least one domain" copy lives once, in the server's `validateDomains`.
    if (!domainsInput.trim()) return
    setResult({ kind: 'submitting' })
    try {
      // Server's parseDomains splits + canonicalizes — send the raw field, no pre-split.
      const res = await onGenerateLicenseKey(domainsInput)
      if ('inputErrors' in res) {
        setResult({ kind: 'inputErrors', message: res.inputErrors.domains })
        return
      }
      onUpdate(res.license)
      setResult({ kind: 'success', addedDomains: res.addedDomains })
      setDomainsInput('')
    } catch (err) {
      // Session expired mid-action: the global `useTelefuncAbort` handler is already reloading
      // to <Login>, and it's an EXPECTED condition — so bow out rather than logging it as a bug
      // (console.error → Sentry) or flashing a generic error the reload would discard anyway.
      if (isNotLoggedInAbort(err)) return
      console.error('onGenerateLicenseKey failed:', err)
      setResult({ kind: 'error', message: describeUnexpectedError(err) })
    }
  }

  return (
    <section style={style.panel}>
      <h2 style={style.h2}>License key</h2>
      {count === 0 ? (
        <p style={style.hint}>You haven't issued any keys yet. Add at least one domain below to get started.</p>
      ) : (
        <>
          <p style={style.caption}>
            Set{' '}
            <a href="https://vike.dev/license" style={linkStyle}>
              <code>+license</code>
            </a>{' '}
            in your <code>+config.js</code> file:
          </p>
          <LicenseSnippet license={license} />
        </>
      )}
      <form onSubmit={submit} style={style.form}>
        <label style={style.field}>
          <div style={style.label}>
            Domains <span style={{ color: '#888' }}>(comma or space-separated)</span>
          </div>
          <input
            type="text"
            required
            value={domainsInput}
            onChange={(e) => {
              setDomainsInput(e.target.value)
              clearWhenSettled(result, setResult, { kind: 'idle' })
            }}
            placeholder="example.com"
            style={style.input}
          />
        </label>
        <button type="submit" disabled={result.kind === 'submitting'} style={style.button}>
          {result.kind === 'submitting' ? 'Generating…' : 'Add to license'}
        </button>
      </form>
      {result.kind === 'success' && result.addedDomains.length > 0 && (
        <p style={style.success}>
          Added {result.addedDomains.length} new domain{result.addedDomains.length === 1 ? '' : 's'} — snippet updated
          above.
        </p>
      )}
      {result.kind === 'success' && result.addedDomains.length === 0 && (
        <p style={style.neutral}>No new domains added — those were already in your license.</p>
      )}
      {result.kind === 'inputErrors' && <p style={style.error}>{result.message}</p>}
      {result.kind === 'error' && <p style={style.error}>{result.message}</p>}
      <p style={style.note}>
        Enter <strong>root domains</strong>: subdomains are included — a key for <code>example.com</code> also covers{' '}
        <code>foo.example.com</code>, <code>bar.example.com</code>, etc.
      </p>
    </section>
  )
}
